"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import type { WeatherData } from "./useAIContextAnalysis";

// WMO Weather Code to Lucide icon name
export const WMO_ICON_MAP: Record<number, string> = {
  0: "Sun",
  1: "Sun",
  2: "CloudSun",
  3: "Cloud",
  45: "CloudFog",
  48: "CloudFog",
  51: "CloudDrizzle",
  53: "CloudDrizzle",
  55: "CloudDrizzle",
  56: "CloudDrizzle",
  57: "CloudDrizzle",
  61: "CloudRain",
  63: "CloudRain",
  65: "CloudRainWind",
  66: "CloudRain",
  67: "CloudRainWind",
  71: "Snowflake",
  73: "Snowflake",
  75: "Snowflake",
  77: "Snowflake",
  80: "CloudRain",
  81: "CloudRain",
  82: "CloudRainWind",
  85: "Snowflake",
  86: "Snowflake",
  95: "CloudLightning",
  96: "CloudLightning",
  99: "CloudLightning",
};

export function getWeatherIcon(code: number): string {
  return WMO_ICON_MAP[code] || "Cloud";
}

interface HistoricalYear {
  year: number;
  temperatureMax: number;
  temperatureMin: number;
  weatherCode: number;
  weatherLabel: string;
}

export interface WeatherAPIData {
  success: boolean;
  current: { temperature: number; weatherCode: number; weatherLabel: string } | null;
  forecast: { date: string; temperatureMax: number; temperatureMin: number; weatherCode: number; weatherLabel: string } | null;
  historical: {
    years: HistoricalYear[];
    averageMax: number;
    averageMin: number;
  };
  targetDate: string;
  dataAge: "forecast" | "historical_only" | "past";
}

interface UseWeatherDataReturn {
  data: WeatherAPIData | null;
  loading: boolean;
  error: string | null;
}

export function useWeatherData(weatherData: WeatherData | undefined): UseWeatherDataReturn {
  const [data, setData] = useState<WeatherAPIData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef<string | null>(null);

  const fetchKey = weatherData && weatherData.location.latitude != null && weatherData.location.longitude != null
    ? `${weatherData.location.latitude.toFixed(2)}:${weatherData.location.longitude.toFixed(2)}:${weatherData.targetDate}`
    : null;

  const fetchWeatherData = useCallback(async () => {
    if (!weatherData || !fetchKey) return;
    if (fetchedRef.current === fetchKey) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        latitude: String(weatherData.location.latitude),
        longitude: String(weatherData.location.longitude),
        targetDate: weatherData.targetDate,
        timezone: weatherData.location.timezone || "UTC",
      });
      if (weatherData.location.city) {
        params.set("city", weatherData.location.city);
      }

      const res = await fetch(`/api/weather/data?${params.toString()}`);
      if (!res.ok) {
        throw new Error("Failed to fetch weather data");
      }

      const result = await res.json();
      if (result.success) {
        setData(result);
        fetchedRef.current = fetchKey;
      } else {
        throw new Error(result.error || "Weather data unavailable");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to fetch weather data";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [weatherData, fetchKey]);

  useEffect(() => {
    if (fetchKey && fetchedRef.current !== fetchKey) {
      fetchWeatherData();
    }
  }, [fetchKey, fetchWeatherData]);

  return { data, loading, error };
}
